import { useEffect, useState } from 'react';
import {
  EMAIL_ONLY_EVENTS,
  NOTIFICATION_EVENT_GROUPS,
  type NotificationSettings,
} from '@forecastify/shared';
import { Button, Checkbox, Select, Switch, TextInput } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { Card, CardColumn, Field } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Notifications — grouped events × two channels, plus the schedule.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A `<table>`, not a grid of divs.** It is a matrix: rows are events, columns are channels, and
 * the cell at their intersection is a checkbox. A screen reader announcing "checkbox, checked" with
 * no idea which row or column it sits in is useless, and `<th scope>` is what fixes that — for free,
 * and only for a real table.
 */

const EVENT_LABEL: Readonly<Record<string, MessageKey>> = {
  assigned: 'account.notif.event.assigned',
  status: 'account.notif.event.status',
  comment: 'account.notif.event.comment',
  due: 'account.notif.event.due',
  approval: 'account.notif.event.approval',
  request: 'account.notif.event.request',
  receipt: 'account.notif.event.receipt',
  project: 'account.notif.event.project',
  alerts: 'account.notif.event.alerts',
  contract: 'account.notif.event.contract',
  logging: 'account.notif.event.logging',
  team: 'account.notif.event.team',
  finance: 'account.notif.event.finance',
  integrations: 'account.notif.event.integrations',
};

const EVENT_HINT: Readonly<Record<string, MessageKey>> = {
  assigned: 'account.notif.event.assignedHint',
  status: 'account.notif.event.statusHint',
  comment: 'account.notif.event.commentHint',
  due: 'account.notif.event.dueHint',
  approval: 'account.notif.event.approvalHint',
  request: 'account.notif.event.requestHint',
  receipt: 'account.notif.event.receiptHint',
  project: 'account.notif.event.projectHint',
  alerts: 'account.notif.event.alertsHint',
  contract: 'account.notif.event.contractHint',
  logging: 'account.notif.event.loggingHint',
  team: 'account.notif.event.teamHint',
  finance: 'account.notif.event.financeHint',
  integrations: 'account.notif.event.integrationsHint',
};

const GROUP_LABEL: Readonly<Record<(typeof NOTIFICATION_EVENT_GROUPS)[number]['id'], MessageKey>> =
  {
    tasks: 'account.notif.group.tasks',
    requests: 'account.notif.group.requests',
    projects: 'account.notif.group.projects',
    time: 'account.notif.group.time',
    workspace: 'account.notif.group.workspace',
  };

const DAY_LABEL: Readonly<Record<number, MessageKey>> = {
  1: 'account.notif.day.1',
  2: 'account.notif.day.2',
  3: 'account.notif.day.3',
  4: 'account.notif.day.4',
  5: 'account.notif.day.5',
  6: 'account.notif.day.6',
  7: 'account.notif.day.7',
};

type Draft = Omit<NotificationSettings, 'version'>;

export function NotificationsSection({
  notifications,
  onSave,
  onDirtyChange,
}: {
  readonly notifications: NotificationSettings;
  readonly onSave?: (patch: unknown) => Promise<void>;
  readonly onDirtyChange?: (dirty: boolean) => void;
}) {
  const t = useT();

  const loaded: Draft = {
    paused: notifications.paused,
    dueLeadDays: notifications.dueLeadDays,
    reminderEnabled: notifications.reminderEnabled,
    reminderAt: notifications.reminderAt,
    reminderDays: notifications.reminderDays,
    weeklyDigest: notifications.weeklyDigest,
    channels: notifications.channels,
  };

  const [draft, setDraft] = useState<Draft>(loaded);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(loaded);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const setChannel = (event: string, channel: 'email' | 'inApp', value: boolean): void => {
    setDraft((current) => ({
      ...current,
      channels: current.channels.map((row) =>
        row.event === event ? { ...row, [channel]: value } : row,
      ),
    }));
  };

  const toggleDay = (day: number): void => {
    setDraft((current) => {
      const has = current.reminderDays.includes(day);
      const next = has
        ? current.reminderDays.filter((value) => value !== day)
        : [...current.reminderDays, day].sort((a, b) => a - b);

      /* ⚠️ Never empty. An empty set is "a reminder that never fires", which is what the switch
       * above is for — and the server refuses it with a message about array length that says
       * nothing about days. */
      return next.length === 0 ? current : { ...current, reminderDays: next };
    });
  };

  const save = (): void => {
    if (onSave === undefined) return;
    setSaving(true);
    void onSave({
      ...draft,
      /* ⚠️ **`inApp` is written as `true` for every event — item 17.** The screen draws it that way
         and offers no control, so a stored `false` from before the ruling would sit there
         disagreeing with what the person is looking at. This is what makes the store catch up. */
      channels: draft.channels.map((channel) => ({ ...channel, inApp: true })),
      version: notifications.version,
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <CardColumn>
      <Card
        title={t('account.notif.title')}
        actions={
          dirty ? (
            <>
              <span className="text-muted-foreground text-[11.5px]">
                {t('account.section.unsaved')}
              </span>
              <Button size="sm" disabled={saving} onClick={save}>
                {saving ? t('account.saving') : t('account.save')}
              </Button>
            </>
          ) : undefined
        }
      >
        {/* ⚠️ **Item 17 · the hint paragraph is gone.** *"Hint text removed, descriptions kept"* —
            the per-row second line under each event survives, because it says what the event IS;
            this one said what a pause switch does, which the switch says. */}
        <div className="flex items-start gap-3 border-b pb-3">
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-medium">{t('account.notif.pause')}</p>
          </div>
          <Switch
            label={t('account.notif.pause')}
            checked={draft.paused}
            onCheckedChange={(next) => {
              setDraft((current) => ({ ...current, paused: next }));
            }}
          />
        </div>

        {/* ⚠️ The matrix stays visible while paused, dimmed rather than hidden: the settings are
            kept, and hiding them would suggest they were lost. */}
        <table
          className={['mt-3 w-full', draft.paused ? 'opacity-50' : ''].join(' ')}
          aria-describedby="notif-paused"
        >
          <caption className="sr-only">{t('account.notif.title')}</caption>
          <thead>
            <tr className="border-b">
              <th
                scope="col"
                className="text-muted-foreground py-1.5 text-left text-[10.5px] font-medium uppercase"
              >
                {t('account.notif.title')}
              </th>
              <th
                scope="col"
                className="text-muted-foreground w-20 py-1.5 text-[10.5px] font-medium uppercase"
              >
                {t('account.notif.channelEmail')}
              </th>
              <th
                scope="col"
                className="text-muted-foreground w-20 py-1.5 text-[10.5px] font-medium uppercase"
              >
                {t('account.notif.channelApp')}
              </th>
            </tr>
          </thead>
          {/* `0182` · one `tbody` per group, each opened by a header row — the mockup's email groups,
              so *"Szabadság és túlóra"* reads as its own block under the task events. */}
          {NOTIFICATION_EVENT_GROUPS.map((group) => (
            <tbody key={group.id}>
              <tr className="border-b">
                <th
                  scope="colgroup"
                  colSpan={3}
                  className="text-muted-foreground pt-3 pb-1.5 text-left text-[10.5px] font-medium uppercase"
                >
                  {t(GROUP_LABEL[group.id])}
                </th>
              </tr>
              {group.events.map((event) => {
                const row = draft.channels.find((candidate) => candidate.event === event);
                const label = t(EVENT_LABEL[event] ?? 'app.name');
                return (
                  <tr key={event} className="border-b last:border-0">
                    {/* ⚠️ `scope="row"`, which is what makes a cell's checkbox announce its event. */}
                    <th scope="row" className="py-2 text-left font-normal">
                      <span className="block text-[12.5px]">{label}</span>
                      <span className="text-muted-foreground block text-[11px]">
                        {t(EVENT_HINT[event] ?? 'app.name')}
                      </span>
                    </th>
                    {/* ⚠️ **Item 17 · `flex justify-center`, not `text-center`.** A `Checkbox` is an
                      inline-flex carrying its own accessible label, so centring the TEXT centred
                      the box plus a label nobody can see — which is why the two columns of ticks
                      did not line up with their headers or with each other. */}
                    <td>
                      <div className="flex justify-center">
                        <Checkbox
                          /* The accessible name still names both axes: a table's row and column
                           * headers are not read by every assistive technology in every mode. */
                          label={`${label}, ${t('account.notif.channelEmail')}`}
                          checked={row?.email ?? false}
                          disabled={draft.paused}
                          onCheckedChange={(next) => {
                            setChannel(event, 'email', next);
                          }}
                        />
                      </div>
                    </td>
                    <td>
                      {EMAIL_ONLY_EVENTS.includes(event) ? (
                        /* ⚠️ A receipt has no bell row — it is a copy of the person's own act — so
                         there is no box to draw, ticked or not. Words, not an empty cell. */
                        <span className="text-muted-foreground block text-center text-[11px]">
                          {t('account.notif.emailOnly')}
                        </span>
                      ) : (
                        <div className="flex justify-center">
                          {/**
                           * ⚠️⚠️ **ALWAYS ON AND READ-ONLY — item 17.** An in-app notification is the
                           * bell in the header; turning it off for an event means the thing happened,
                           * the product knows, and it says nothing anywhere. The mail channel is the
                           * one that is genuinely a preference, because it leaves the product.
                           *
                           * ⚠️ **Rendered checked rather than reading the row.** A stored `false` from
                           * before this ruling would otherwise draw an unticked box that nothing can
                           * tick — `save()` writes `true` for every event, so the store catches up the
                           * first time anything on this card is saved.
                           */}
                          <Checkbox
                            label={`${label}, ${t('account.notif.channelApp')}`}
                            checked
                            disabled
                            onCheckedChange={() => undefined}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </Card>

      <Card title={t('account.notif.reminder')}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="flex-1 text-[12.5px]">{t('account.notif.reminder')}</span>
            <Switch
              label={t('account.notif.reminder')}
              checked={draft.reminderEnabled}
              onCheckedChange={(next) => {
                setDraft((current) => ({ ...current, reminderEnabled: next }));
              }}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('account.notif.reminderAt')}>
              <TextInput
                type="time"
                value={draft.reminderAt}
                disabled={!draft.reminderEnabled}
                onChange={(event) => {
                  setDraft((current) => ({ ...current, reminderAt: event.target.value }));
                }}
              />
            </Field>

            <Field label={t('account.notif.lead')}>
              <Select
                value={String(draft.dueLeadDays)}
                options={[1, 2, 3, 5, 7, 14].map((days) => ({
                  value: String(days),
                  label: String(days),
                }))}
                onChange={(event) => {
                  setDraft((current) => ({ ...current, dueLeadDays: Number(event.target.value) }));
                }}
              />
            </Field>
          </div>

          <fieldset disabled={!draft.reminderEnabled}>
            <legend className="text-[12px] font-medium">{t('account.notif.reminderDays')}</legend>
            <div className="mt-1.5 flex gap-1">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                const on = draft.reminderDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    /* ⚠️ `aria-pressed`, because this is a toggle and not a link. Colour alone
                     * cannot say "Monday is on". */
                    aria-pressed={on}
                    onClick={() => {
                      toggleDay(day);
                    }}
                    className={[
                      'h-7 min-w-9 rounded-md border px-2 text-[11.5px] transition-colors',
                      on
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-input text-muted-foreground hover:bg-muted',
                    ].join(' ')}
                  >
                    {t(DAY_LABEL[day] ?? 'app.name')}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="flex items-center gap-3 border-t pt-3">
            <span className="flex-1 text-[12.5px]">{t('account.notif.weekly')}</span>
            <Switch
              label={t('account.notif.weekly')}
              checked={draft.weeklyDigest}
              onCheckedChange={(next) => {
                setDraft((current) => ({ ...current, weeklyDigest: next }));
              }}
            />
          </div>
        </div>
      </Card>
    </CardColumn>
  );
}
